import axiosClient from './axiosClient';

/**
 * Typed axios wrappers for `/api/admin/users` + `/api/admin/roles`.
 *
 * Admin users surface (UserAdminPage) reads and writes the 3 tables that
 * drive platform access:
 *   - config.app_user       (the invitee / logged-in identity)
 *   - config.app_role       (system + custom roles)
 *   - config.app_user_role  (grants: user ↔ role)
 *
 * Two system roles (`default`, `super-admin`) are bootstrapped by migration
 * — they're flagged `is_system=true` and cannot be renamed, re-described,
 * or deleted. Guardrails are enforced server-side; the client mirrors them
 * to avoid a failed round-trip.
 */

/* ------------------------------------------------------------------ */
/*  Users                                                              */
/* ------------------------------------------------------------------ */

/**
 * An application user. `auth0_sub` is NULL until the user actually logs in
 * for the first time — the admin creates the email and the sub gets
 * backfilled on login via the `/api/me` bootstrap.
 */
export interface AdminUser {
  user_id: number;
  email: string;
  display_name: string | null;
  /** Null = invited but never logged in ("Pending first login"). */
  auth0_sub: string | null;
  /**
   * Write-once, never cleared once set (unlike auth0_sub, which
   * PATCH /:id/reset-sub deliberately clears for re-binding). This — not
   * auth0_sub — is the correct signal for "has this user ever logged in,"
   * since a null auth0_sub after a reset-sub does NOT mean the user never
   * logged in.
   */
  first_login_ts: string | null;
  active: boolean;
  /** Primary (collapsed) role code — kept for back-compat / summary display. */
  role_code: string;
  role_name: string;
  /** Full role set the multi-role editor binds to. */
  roles: string[];
  /**
   * RBAC_V2 (architecture R9): this user's v2 role code, LEFT-JOINed onto
   * the list response (Default fallback when unassigned) so the v2 Users
   * tab can render the role cell without a per-row fetch. Absent when
   * RBAC_V2_ENABLED is off — the legacy page never reads these two fields.
   */
  role_v2_code?: string;
  role_v2_name?: string;
  created_at: string;
  updated_at: string | null;
  last_login_at: string | null;
}

export interface ListUsersResponse {
  rows: AdminUser[];
}

export interface CreateUserPayload {
  email: string;
  display_name?: string | null;
  role_code: string;
}

/**
 * POST /api/admin/users' actual response shape (server/src/routes/admin/
 * users.ts's `router.post('/')`) — a fresh row, not the list-query
 * projection `AdminUser` binds to. No `user_id` (it's `id`), no `auth0_sub`
 * / `roles` / `last_login_at` / timestamps — those only exist once the row
 * has been through the list query at least once.
 */
export interface CreatedUser {
  id: number;
  email: string;
  display_name: string | null;
  role_code: string;
  active: boolean;
}

export interface UpdateUserPayload {
  display_name?: string | null;
  /** Login lookup key — case-insensitive unique. Collision → 409 email_exists. */
  email?: string;
  role_code?: string;
  active?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Roles                                                              */
/* ------------------------------------------------------------------ */

export interface AdminRole {
  role_code: string;
  role_name: string;
  description: string | null;
  /**
   * Bootstrapped system roles (default, super-admin) are read-only for
   * rename/describe and un-deletable. UI renders a lock icon on these.
   */
  is_system: boolean;
  /** How many users currently hold this role — disables delete when > 0. */
  grant_count: number;
  created_at: string;
  updated_at: string | null;
}

export interface ListRolesResponse {
  rows: AdminRole[];
}

export interface CreateRolePayload {
  role_code: string;
  role_name: string;
  description?: string | null;
}

export interface UpdateRolePayload {
  role_name?: string;
  description?: string | null;
}

/* ------------------------------------------------------------------ */
/*  API                                                                */
/* ------------------------------------------------------------------ */

const userBase = '/api/admin/users';
// /roles is mounted as a sub-path of /users (see server/src/routes/admin/users.ts).
// Full URL is /api/admin/users/roles, not /api/admin/roles.
const roleBase = '/api/admin/users/roles';

export const adminUsersApi = {
  /* ------------------ Users ------------------ */

  listUsers: async (): Promise<AdminUser[]> => {
    const res = await axiosClient.get<ListUsersResponse>(userBase);
    return res.data.rows;
  },

  createUser: async (payload: CreateUserPayload): Promise<CreatedUser> => {
    const res = await axiosClient.post<CreatedUser>(userBase, payload);
    return res.data;
  },

  updateUser: async (
    userId: number,
    payload: UpdateUserPayload,
  ): Promise<AdminUser> => {
    const res = await axiosClient.patch<AdminUser>(
      `${userBase}/${userId}`,
      payload,
    );
    return res.data;
  },

  /**
   * Set a user's FULL role set (multi-role). Replaces all current grants with
   * `roles`. Backend guardrails: cannot self-demote from super-admin, cannot
   * drop the last active super-admin.
   */
  updateRoles: async (
    userId: number,
    roles: string[],
  ): Promise<{ id: number; roles: string[] }> => {
    const res = await axiosClient.patch<{ id: number; roles: string[] }>(
      `${userBase}/${userId}/roles`,
      { roles },
    );
    return res.data;
  },

  /** Soft-delete — keeps the row for audit, flips `active=false`. */
  deactivateUser: async (userId: number): Promise<AdminUser> => {
    const res = await axiosClient.patch<AdminUser>(`${userBase}/${userId}`, {
      active: false,
    });
    return res.data;
  },

  reactivateUser: async (userId: number): Promise<AdminUser> => {
    const res = await axiosClient.patch<AdminUser>(`${userBase}/${userId}`, {
      active: true,
    });
    return res.data;
  },

  /** Hard-delete — only allowed for users that have never logged in. */
  removeUser: async (userId: number): Promise<void> => {
    await axiosClient.delete(`${userBase}/${userId}`);
  },

  /* ------------------ Roles ------------------ */

  listRoles: async (): Promise<AdminRole[]> => {
    const res = await axiosClient.get<ListRolesResponse>(roleBase);
    return res.data.rows;
  },

  createRole: async (payload: CreateRolePayload): Promise<AdminRole> => {
    const res = await axiosClient.post<AdminRole>(roleBase, payload);
    return res.data;
  },

  updateRole: async (
    code: string,
    payload: UpdateRolePayload,
  ): Promise<AdminRole> => {
    const res = await axiosClient.patch<AdminRole>(
      `${roleBase}/${encodeURIComponent(code)}`,
      payload,
    );
    return res.data;
  },

  removeRole: async (code: string): Promise<void> => {
    await axiosClient.delete(`${roleBase}/${encodeURIComponent(code)}`);
  },
};
